import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../hooks/useApi';
import {
  CurrencyIcon, ReceiptIcon, TrendUpIcon, RefreshIcon,
  UsersIcon, CheckIcon, AlertIcon, ClockIcon, WalletIcon,
} from '../../components/Icons';

const fmt = v => parseFloat(v || 0).toLocaleString('pt-PT');


function BadgeEstado({ estado }) {
  const map = {
    pendente:         { label: 'Pendente',         bg: '#FFFBEB', color: '#D97706' },
    em_processamento: { label: 'Em processamento', bg: '#EFF6FF', color: '#2563EB' },
    pago:             { label: 'Pago',             bg: '#F0FDF4', color: '#16A34A' },
    rejeitado:        { label: 'Rejeitado',        bg: '#FEF2F2', color: '#DC2626' },
  };
  const s = map[estado] || { label: estado, bg: '#F3F4F6', color: '#6B7280' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: '600', background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function ModalPagar({ selecionados, onClose, onSucesso }) {
  const total      = selecionados.reduce((s, r) => s + parseFloat(r.valor), 0);
  const [ref,      setRef]      = useState('');
  const [notas,    setNotas]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [erro,     setErro]     = useState(null);

  const executar = async () => {
    setLoading(true);
    setErro(null);
    try {
      const data = await apiFetch(
        '/admin/saldos/pedidos/pagar',
        'POST',
        { ids: selecionados.map(s => s.id), referencia: ref, notas }
      );
      if (data.sucesso) {
        onSucesso(data.mensagem);
        onClose();
      } else {
        setErro(data.mensagem || 'Erro ao processar');
      }
    } catch (e) {
      setErro(e.message || 'Erro de ligação ao servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}>
      <div className="card" style={{ width: '100%', maxWidth: '520px', padding: '24px' }}>
        <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>
          Confirmar Pagamento Consolidado
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px' }}>
          {selecionados.length} pedido(s) seleccionado(s) de {[...new Set(selecionados.map(s => s.fornecedor_id))].length} fornecedor(es)
        </div>

        {/* Resumo financeiro */}
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Total a transferir ao(s) fornecedor(es)</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--success)' }}>{fmt(total)} Kz</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Valor integral — taxa de transferência suportada pela KiAluga</div>
        </div>

        {/* Lista dos pedidos */}
        <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '14px', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          {selecionados.map((s, i) => (
            <div key={s.id} style={{
              padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderBottom: i < selecionados.length - 1 ? '1px solid var(--border)' : 'none',
              fontSize: '12px',
            }}>
              <div>
                <div style={{ fontWeight: '600' }}>{s.fornecedor_nome}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{s.banco} · {s.iban}</div>
              </div>
              <div style={{ fontWeight: '700', color: 'var(--success)' }}>{fmt(s.valor)} Kz</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Referência da transferência (opcional)</label>
            <input className="input" value={ref} onChange={e => setRef(e.target.value)} placeholder="Ex: TRF-KA-2024-001" />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Notas internas (opcional)</label>
            <textarea className="input" value={notas} onChange={e => setNotas(e.target.value)} rows={2} style={{ resize: 'none' }} placeholder="Observações sobre este pagamento..." />
          </div>
        </div>

        {erro && (
          <div style={{
            marginTop: '12px', padding: '10px 14px', borderRadius: 'var(--radius)',
            background: '#FEF2F2', color: '#DC2626', fontSize: '12px',
            border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <AlertIcon size={13} color="#DC2626" /> {erro}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={onClose} disabled={loading}>Cancelar</button>
          <button className="btn btn-primary" onClick={executar} disabled={loading} style={{ background: 'var(--success)', minWidth: '160px' }}>
            {loading
              ? <><ClockIcon size={13} /> A processar...</>
              : <><CheckIcon size={13} color="#fff" /> Confirmar pagamento</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalRejeitar({ pedido, onClose, onSucesso }) {
  const [notas,   setNotas]   = useState('');
  const [loading, setLoading] = useState(false);
  const [erro,    setErro]    = useState(null);

  const rejeitar = async () => {
    setLoading(true);
    setErro(null);
    try {
      const data = await apiFetch(
        `/admin/saldos/pedidos/${pedido.id}/rejeitar`,
        'PATCH',
        { notas_admin: notas }
      );
      if (data.sucesso) { onSucesso(data.mensagem); onClose(); }
      else setErro(data.mensagem || 'Erro ao rejeitar');
    } catch (e) {
      setErro(e.message || 'Erro de ligação ao servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1010, padding: '20px',
    }}>
      <div className="card" style={{ width: '100%', maxWidth: '400px', padding: '24px' }}>
        <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px' }}>Rejeitar pedido</div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
          {pedido.fornecedor_nome} — {fmt(pedido.valor)} Kz
        </div>
        <div>
          <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Motivo da rejeição (opcional)</label>
          <textarea className="input" value={notas} onChange={e => setNotas(e.target.value)} rows={3} style={{ resize: 'none' }} placeholder="Explica o motivo ao fornecedor..." />
        </div>
        {erro && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: '#FEF2F2', color: '#DC2626', borderRadius: 'var(--radius)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertIcon size={12} color="#DC2626" /> {erro}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={onClose} disabled={loading}>Cancelar</button>
          <button className="btn btn-primary" onClick={rejeitar} disabled={loading} style={{ background: 'var(--danger)', minWidth: '120px' }}>
            {loading ? <><ClockIcon size={13} /> A rejeitar...</> : 'Rejeitar pedido'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de detalhe: histórico completo de um fornecedor ──
function ModalDetalheWallet({ walletId, onClose }) {
  const [wallet,       setWallet]       = useState(null);
  const [transacoes,   setTransacoes]   = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [load,         setLoad]         = useState(true);
  const [subTab,       setSubTab]       = useState('movimentos');

  useEffect(() => {
    (async () => {
      setLoad(true);
      try {
        const data = await apiFetch(`/admin/saldos/${walletId}/movimentos`);
        if (data.sucesso) {
          setWallet(data.wallet);
          setTransacoes(data.transacoes || []);
          setSolicitacoes(data.solicitacoes || []);
        }
      } catch {}
      finally { setLoad(false); }
    })();
  }, [walletId]);

  const saldo           = parseFloat(wallet?.saldo || 0);
  const saldoSolicitado = parseFloat(wallet?.saldo_solicitado || 0);
  const disponivel      = Math.max(saldo - saldoSolicitado, 0);

  const fmtData = (d) => {
    const dt = new Date(d);
    return dt.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + dt.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--card)', borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        width: '100%', maxWidth: '780px',
        maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>

        {/* ── Header ────────────────────────────── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {wallet?.fornecedor_foto
                ? <img src={wallet.fornecedor_foto} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: 'var(--orange)' }}>
                    {(wallet?.fornecedor_nome || '?')[0].toUpperCase()}
                  </div>
              }
              {wallet?.fornecedor_nome || 'A carregar...'}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', marginLeft: '42px' }}>
              {wallet?.fornecedor_email} {wallet?.fornecedor_telefone ? ` · ${wallet.fornecedor_telefone}` : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '30px', height: '30px', borderRadius: '50%', border: 'none',
              background: 'var(--surface)', cursor: 'pointer', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px', color: 'var(--text-muted)', marginLeft: '12px',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
            title="Fechar"
          >×</button>
        </div>

        {/* ── Body ──────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {load ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>A carregar...</div>
          ) : (
            <>
              {/* KPIs resumo */}
              <div className="grid-4" style={{ marginBottom: '18px' }}>
                {[
                  { label: 'Saldo actual',    val: `${fmt(saldo)} Kz`,                           cor: 'var(--text-primary)' },
                  { label: 'Disponível',       val: `${fmt(disponivel)} Kz`,                      cor: 'var(--success)' },
                  { label: 'Total recebido',   val: `${fmt(wallet?.total_recebido)} Kz`,          cor: '#2563EB' },
                  { label: 'Total levantado',  val: `${fmt(wallet?.total_pago)} Kz`,              cor: '#F97316' },
                ].map((c, i) => (
                  <div key={i} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '10px 12px' }}>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{c.label}</div>
                    <div style={{ fontSize: '17px', fontWeight: '800', color: c.cor, marginTop: '2px' }}>{c.val}</div>
                  </div>
                ))}
              </div>

              {/* Dados bancários */}
              {wallet?.banco && (
                <div style={{
                  background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 'var(--radius)',
                  padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#1D4ED8',
                  display: 'flex', gap: '16px', flexWrap: 'wrap',
                }}>
                  <div><strong>Banco:</strong> {wallet.banco}</div>
                  <div><strong>IBAN:</strong> <span style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>{wallet.iban || '—'}</span></div>
                  <div><strong>Titular:</strong> {wallet.titular || '—'}</div>
                </div>
              )}

              {/* Sub-tabs */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                {[
                  { key: 'movimentos',   label: `Movimentos (${transacoes.length})` },
                  { key: 'levantamentos', label: `Levantamentos (${solicitacoes.length})` },
                ].map(t => (
                  <button
                    key={t.key}
                    onClick={() => setSubTab(t.key)}
                    style={{
                      padding: '6px 14px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
                      fontSize: '11px', fontWeight: subTab === t.key ? '600' : '400',
                      background: subTab === t.key ? 'var(--orange)' : 'var(--surface)',
                      color: subTab === t.key ? '#fff' : 'var(--text-secondary)',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Sub-tab: Movimentos */}
              {subTab === 'movimentos' && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                  <table className="data-table" style={{ fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th>Data / Hora</th>
                        <th>Tipo</th>
                        <th>Descrição</th>
                        <th style={{ textAlign: 'right' }}>Valor</th>
                        <th style={{ textAlign: 'right' }}>Saldo após</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transacoes.length === 0 ? (
                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>Sem movimentos registados</td></tr>
                      ) : transacoes.map(t => (
                        <tr key={t.id}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '11px', color: 'var(--text-muted)' }}>
                            {fmtData(t.created_at)}
                          </td>
                          <td>
                            <span style={{
                              padding: '2px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: '600',
                              background: t.tipo === 'credito' ? '#F0FDF4' : t.tipo === 'debito' ? '#FFF7ED' : '#F3F4F6',
                              color:      t.tipo === 'credito' ? '#16A34A' : t.tipo === 'debito' ? '#EA580C' : '#6B7280',
                            }}>
                              {t.tipo === 'credito' ? 'Crédito' : t.tipo === 'debito' ? 'Levantamento' : t.tipo}
                            </span>
                          </td>
                          <td>
                            <div style={{ fontWeight: '500' }}>{t.descricao || '—'}</div>
                            {t.anuncio_titulo && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Anúncio: {t.anuncio_titulo}</div>}
                          </td>
                          <td style={{
                            textAlign: 'right', fontWeight: '700', whiteSpace: 'nowrap',
                            color: t.tipo === 'credito' ? '#16A34A' : '#EA580C',
                          }}>
                            {t.tipo === 'credito' ? '+' : '-'}{fmt(t.valor)} Kz
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>
                            {fmt(t.saldo_apos)} Kz
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Sub-tab: Levantamentos */}
              {subTab === 'levantamentos' && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                  <table className="data-table" style={{ fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th>Data pedido</th>
                        <th>Valor</th>
                        <th>Banco / IBAN</th>
                        <th>Estado</th>
                        <th>Referência</th>
                        <th>Processado por</th>
                        <th>Data processamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {solicitacoes.length === 0 ? (
                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>Sem pedidos de levantamento</td></tr>
                      ) : solicitacoes.map(s => (
                        <tr key={s.id}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '11px', color: 'var(--text-muted)' }}>
                            {fmtData(s.created_at)}
                          </td>
                          <td style={{
                            fontWeight: '700', whiteSpace: 'nowrap',
                            color: s.estado === 'pago' ? '#16A34A' : s.estado === 'rejeitado' ? '#DC2626' : 'var(--text-primary)',
                          }}>
                            {fmt(s.valor)} Kz
                          </td>
                          <td style={{ fontSize: '11px' }}>
                            <div style={{ fontWeight: '500' }}>{s.banco || '—'}</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: '10px', fontFamily: 'monospace' }}>{s.iban || ''}</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{s.titular || ''}</div>
                          </td>
                          <td><BadgeEstado estado={s.estado} /></td>
                          <td style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {s.referencia || '—'}
                          </td>
                          <td style={{ fontSize: '11px' }}>
                            {s.processado_por_nome || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '11px', color: 'var(--text-muted)' }}>
                            {s.estado !== 'pendente' && s.updated_at ? fmtData(s.updated_at) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Notas do admin (se existirem) */}
              {subTab === 'levantamentos' && solicitacoes.some(s => s.notas_admin) && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: 'var(--surface)', borderRadius: 'var(--radius)', fontSize: '11px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '6px', color: 'var(--text-secondary)' }}>Notas do admin nos pedidos:</div>
                  {solicitacoes.filter(s => s.notas_admin).map(s => (
                    <div key={s.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtData(s.updated_at || s.created_at)}</span>
                      <span>{s.notas_admin}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ────────────────────────────── */}
        <div style={{
          padding: '14px 24px',
          borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end',
          flexShrink: 0,
        }}>
          <button className="btn btn-outline" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

export default function WalletsAdmin() {
  const [wallets,   setWallets]   = useState([]);
  const [pedidos,   setPedidos]   = useState([]);
  const [totais,    setTotais]    = useState({});
  const [load,      setLoad]      = useState(true);
  const [tab,       setTab]       = useState('pedidos');
  const [selec,     setSelec]     = useState([]);        // IDs seleccionados
  const [modalPag,  setModalPag]  = useState(false);
  const [modalRej,  setModalRej]  = useState(null);
  const [toast,     setToast]     = useState(null);
  const [pesquisa,  setPesquisa]  = useState('');
  const [detalhe,   setDetalhe]   = useState(null);  // wallet id para modal detalhe

  const carregar = useCallback(async () => {
    setLoad(true);
    try {
      const data = await apiFetch('/admin/saldos');
      if (data.sucesso) {
        setWallets(data.wallets || []);
        setTotais(data.totais || {});
        setPedidos(data.pedidos_pendentes || []);
        setSelec([]);
      }
    } catch {}
    finally { setLoad(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const mostrarToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const toggleSelec = (id) => {
    setSelec(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleTodos = () => {
    const pendentes = pedidos.filter(p => p.estado === 'pendente');
    setSelec(selec.length === pendentes.length ? [] : pendentes.map(p => p.id));
  };

  const pedidosSelec      = pedidos.filter(p => selec.includes(p.id));
  const totalSelec        = pedidosSelec.reduce((s, p) => s + parseFloat(p.valor), 0);
  const totalPendente     = pedidos.reduce((s, p) => s + parseFloat(p.valor), 0);

  const walletsFiltradas = wallets.filter(w =>
    !pesquisa ||
    w.fornecedor_nome?.toLowerCase().includes(pesquisa.toLowerCase()) ||
    w.fornecedor_email?.toLowerCase().includes(pesquisa.toLowerCase())
  );

  return (
    <>
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 2000,
          background: '#16A34A', color: '#fff', padding: '12px 18px',
          borderRadius: 'var(--radius-lg)', fontSize: '13px', fontWeight: '600',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <CheckIcon size={14} color="#fff" /> {toast}
        </div>
      )}

      {modalPag && (
        <ModalPagar
          selecionados={pedidosSelec}
          onClose={() => setModalPag(false)}
          onSucesso={msg => { mostrarToast(msg); carregar(); }}
        />
      )}

      {modalRej && (
        <ModalRejeitar
          pedido={modalRej}
          onClose={() => setModalRej(null)}
          onSucesso={msg => { mostrarToast(msg); carregar(); }}
        />
      )}

      {detalhe && (
        <ModalDetalheWallet
          walletId={detalhe}
          onClose={() => setDetalhe(null)}
        />
      )}

      <div className="topbar">
        <div>
          <div className="topbar-title">Saldos de Fornecedores</div>
          <div className="topbar-sub">Saldos a receber · Pedidos de levantamento · Pagamento consolidado manual</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-outline" onClick={carregar} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <RefreshIcon size={14} /> Actualizar
          </button>
        </div>
      </div>

      <div className="page-content">

        {/* ── KPIs ─────────────────────────────────────────── */}
        <div className="stats-grid stats-grid-5" style={{ marginBottom: '20px' }}>
          {[
            {
              icon: <WalletIcon />, label: 'Saldo retido (total)',
              value: `${fmt(totais.total_saldo)} Kz`,
              sub: `${parseInt(totais.total_wallets || 0)} fornecedores`,
              bg: '#EFF6FF', ic: '#2563EB',
            },
            {
              icon: <ClockIcon />, label: 'Pedidos pendentes',
              value: `${fmt(totais.total_solicitado)} Kz`,
              sub: `${pedidos.length} pedido(s) a aguardar`,
              bg: '#FFFBEB', ic: '#D97706',
            },
            {
              icon: <TrendUpIcon />, label: 'Total recebido',
              value: `${fmt(totais.total_recebido)} Kz`,
              sub: 'Acumulado desde o início',
              bg: '#F0FDF4', ic: '#16A34A',
            },
            {
              icon: <ReceiptIcon />, label: 'Total já pago',
              value: `${fmt(totais.total_pago)} Kz`,
              sub: 'Transferido aos fornecedores',
              bg: '#FFF7ED', ic: '#F97316',
            },
          ].map((c, i) => (
            <div key={i} className="stat-card">
              <div className="stat-icon" style={{ background: c.bg }}>
                {React.cloneElement(c.icon, { size: 18, color: c.ic })}
              </div>
              <div className="stat-value" style={{ marginTop: '8px' }}>{c.value}</div>
              <div className="stat-label">{c.label}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{c.sub}</div>
            </div>
          ))}
        </div>


        {/* ── Tabs ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
          {[
            { key: 'pedidos',   label: `Pedidos pendentes${pedidos.length > 0 ? ` (${pedidos.length})` : ''}` },
            { key: 'wallets',   label: 'Saldos dos fornecedores' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '7px 16px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: tab === t.key ? '600' : '400',
                background: tab === t.key ? 'var(--orange)' : 'var(--surface)',
                color: tab === t.key ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab: Pedidos pendentes ────────────────────────── */}
        {tab === 'pedidos' && (
          <>
            {/* Barra de acção quando há seleccionados */}
            {selec.length > 0 && (
              <div style={{
                background: 'var(--brand-dark)', borderRadius: 'var(--radius-lg)',
                padding: '12px 16px', marginBottom: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
              }}>
                <div style={{ color: '#fff', fontSize: '13px' }}>
                  <strong>{selec.length}</strong> pedido(s) seleccionado(s) —{' '}
                  <strong style={{ color: '#86EFAC' }}>
                    {fmt(totalSelec)} Kz
                  </strong>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => setModalPag(true)}
                  style={{ background: 'var(--success)', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
                >
                  <CheckIcon size={13} color="#fff" /> Pagar seleccionados
                </button>
              </div>
            )}

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CurrencyIcon size={15} color="var(--orange)" />
                  <span style={{ fontSize: '13px', fontWeight: '600' }}>
                    {pedidos.length} pedido(s) a aguardar pagamento · Total: {fmt(totalPendente)} Kz
                  </span>
                </div>
                {pedidos.filter(p => p.estado === 'pendente').length > 0 && (
                  <button
                    className="btn btn-outline"
                    onClick={toggleTodos}
                    style={{ fontSize: '11px', padding: '4px 12px' }}
                  >
                    {selec.length === pedidos.filter(p => p.estado === 'pendente').length ? 'Desseleccionar todos' : 'Seleccionar todos'}
                  </button>
                )}
              </div>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '32px' }}></th>
                      <th>Fornecedor</th>
                      <th>Valor</th>
                      <th>Banco / IBAN</th>
                      <th>Data do pedido</th>
                      <th>Estado</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {load ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>A carregar...</td></tr>
                    ) : pedidos.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                        <CheckIcon size={28} color="var(--border)" /><br />
                        <span style={{ marginTop: '8px', display: 'block', fontSize: '13px' }}>Sem pedidos pendentes</span>
                      </td></tr>
                    ) : pedidos.map(p => (
                      <tr key={p.id} style={{ background: selec.includes(p.id) ? 'rgba(249,115,22,0.04)' : undefined }}>
                        <td>
                          {p.estado === 'pendente' && (
                            <input
                              type="checkbox"
                              checked={selec.includes(p.id)}
                              onChange={() => toggleSelec(p.id)}
                              style={{ cursor: 'pointer', accentColor: 'var(--orange)' }}
                            />
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '50%', background: 'var(--surface)',
                              overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center',
                              justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: 'var(--orange)',
                            }}>
                              {p.fornecedor_foto
                                ? <img src={p.fornecedor_foto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : (p.fornecedor_nome || '?')[0].toUpperCase()
                              }
                            </div>
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: '600' }}>{p.fornecedor_nome}</div>
                              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{p.fornecedor_email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: '14px', fontWeight: '700', color: 'var(--success)', whiteSpace: 'nowrap' }}>
                          {fmt(p.valor)} Kz
                        </td>
                        <td style={{ fontSize: '11px' }}>
                          <div style={{ fontWeight: '600' }}>{p.banco || '—'}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '10px', fontFamily: 'monospace' }}>{p.iban || ''}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{p.titular || ''}</div>
                        </td>
                        <td style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(p.created_at).toLocaleDateString('pt-PT')}<br />
                          {new Date(p.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td><BadgeEstado estado={p.estado} /></td>
                        <td>
                          {p.estado === 'pendente' && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                className="btn btn-primary"
                                style={{ fontSize: '10px', padding: '4px 10px', background: 'var(--success)' }}
                                onClick={() => { setSelec([p.id]); setModalPag(true); }}
                              >
                                Pagar
                              </button>
                              <button
                                className="btn btn-outline"
                                style={{ fontSize: '10px', padding: '4px 10px', color: 'var(--danger)', borderColor: 'var(--danger)' }}
                                onClick={() => setModalRej(p)}
                              >
                                Rejeitar
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── Tab: Saldos overview ──────────────────────────── */}
        {tab === 'wallets' && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <input
                className="input"
                placeholder="Pesquisar fornecedor..."
                value={pesquisa}
                onChange={e => setPesquisa(e.target.value)}
                style={{ maxWidth: '360px' }}
              />
            </div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <WalletIcon size={15} color="var(--orange)" />
                <span style={{ fontSize: '13px', fontWeight: '600' }}>Saldos a Receber — {walletsFiltradas.length} fornecedor(es)</span>
              </div>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fornecedor</th>
                      <th>Saldo total</th>
                      <th>Em pedidos</th>
                      <th>Disponível</th>
                      <th>Total recebido</th>
                      <th>Total pago</th>
                      <th>Banco</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {load ? (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>A carregar...</td></tr>
                    ) : walletsFiltradas.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Sem saldos registados</td></tr>
                    ) : walletsFiltradas.map(w => {
                      const disponivel = Math.max(parseFloat(w.saldo || 0) - parseFloat(w.saldo_solicitado || 0), 0);
                      return (
                        <tr key={w.id} onClick={() => setDetalhe(w.id)} style={{ cursor: 'pointer' }} title="Clica para ver detalhe completo">
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '28px', height: '28px', borderRadius: '50%', background: 'var(--surface)',
                                overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: 'var(--orange)',
                              }}>
                                {w.fornecedor_foto
                                  ? <img src={w.fornecedor_foto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  : (w.fornecedor_nome || '?')[0].toUpperCase()
                                }
                              </div>
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '600' }}>{w.fornecedor_nome}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{w.fornecedor_email}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ fontSize: '13px', fontWeight: '700' }}>{fmt(w.saldo)} Kz</td>
                          <td style={{ fontSize: '12px', color: 'var(--warning)' }}>
                            {parseFloat(w.saldo_solicitado || 0) > 0
                              ? <><ClockIcon size={11} /> {fmt(w.saldo_solicitado)} Kz</>
                              : <span style={{ color: 'var(--text-muted)' }}>—</span>
                            }
                          </td>
                          <td style={{ fontSize: '13px', fontWeight: '700', color: 'var(--success)' }}>{fmt(disponivel)} Kz</td>
                          <td style={{ fontSize: '12px' }}>{fmt(w.total_recebido)} Kz</td>
                          <td style={{ fontSize: '12px' }}>{fmt(w.total_pago)} Kz</td>
                          <td style={{ fontSize: '11px' }}>
                            <div>{w.banco || <span style={{ color: 'var(--text-muted)' }}>—</span>}</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: '10px', fontFamily: 'monospace' }}>
                              {w.iban ? w.iban.substring(0, 20) + '…' : ''}
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-outline"
                              style={{ fontSize: '10px', padding: '4px 10px', whiteSpace: 'nowrap' }}
                              onClick={e => { e.stopPropagation(); setDetalhe(w.id); }}
                            >
                              <ReceiptIcon size={11} /> Ver detalhe
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

      </div>
    </>
  );
}
